import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Bookmark, 
  FileText, 
  Copy, 
  Share2, 
  Image, 
  GitCompare, 
  Highlighter,
  Volume2,
  Sparkles,
  Send,
  X
} from 'lucide-react';
import { Button } from './ui/button';

/**
 * VerseActionMenu - YouVersion-style interactive verse actions
 * Hybrid display: Bottom sheet on mobile, Floating menu on desktop
 */
const VerseActionMenu = ({ 
  isOpen,
  verse,
  onClose,
  onSave,
  onNote,
  onCopy,
  onShare,
  onImage,
  onCompare,
  onHighlight,
  onAudio,
  onPost,
  position = 'bottom' // 'bottom' for mobile, 'floating' for desktop
}) => {
  const { language } = useLanguage();
  const menuRef = useRef(null);

  // Click outside to close - MUST be before early return
  useEffect(() => {
    if (!isOpen || !verse) return;
    
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };
    const handleEsc = (event) => { if (event.key === 'Escape') onClose(); };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, verse, onClose]);

  // Don't render if not open
  if (!isOpen || !verse) return null;

  // Helper: parse reference like "1 Samuel 3:16" -> {book, chapter, verse}
  const parseRef = (ref) => {
    if (!ref) return null;
    const m = String(ref).match(/^(.+)\s+(\d+):(\d+)/);
    if (!m) return null;
    return { book: m[1].trim(), chapter: parseInt(m[2], 10), verse: parseInt(m[3], 10) };
  };

  const handleVerseLens = () => {
    const parsed = parseRef(verse.reference);
    if (!parsed) return;
    window.dispatchEvent(new CustomEvent('lumera:verse-lens', { detail: parsed }));
    onClose();
  };

  // Determine if mobile based on window width
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const actions = [
    {
      id: 'lens',
      icon: Sparkles,
      label: language === 'es' ? 'Lente' : 'Verse Lens',
      onClick: handleVerseLens,
      color: 'text-indigo-600'
    },
    {
      id: 'save',
      icon: Bookmark,
      label: language === 'es' ? 'Guardar' : 'Save',
      onClick: onSave,
      color: 'text-blue-600'
    },
    {
      id: 'note',
      icon: FileText,
      label: language === 'es' ? 'Nota' : 'Note',
      onClick: onNote,
      color: 'text-green-600'
    },
    {
      id: 'copy',
      icon: Copy,
      label: language === 'es' ? 'Copiar' : 'Copy',
      onClick: onCopy,
      color: 'text-slate-600'
    },
    {
      id: 'share',
      icon: Share2,
      label: language === 'es' ? 'Compartir' : 'Share',
      onClick: onShare,
      color: 'text-purple-600'
    },
    {
      id: 'post',
      icon: Send,
      label: language === 'es' ? 'Publicar' : 'Post',
      onClick: onPost,
      color: 'text-fuchsia-600'
    },
    {
      id: 'image',
      icon: Image,
      label: language === 'es' ? 'Imagen' : 'Image',
      onClick: onImage,
      color: 'text-pink-600'
    },
    {
      id: 'compare',
      icon: GitCompare,
      label: language === 'es' ? 'Comparar' : 'Compare',
      onClick: onCompare,
      color: 'text-orange-600'
    },
    {
      id: 'highlight',
      icon: Highlighter,
      label: language === 'es' ? 'Resaltar' : 'Highlight',
      onClick: onHighlight,
      color: 'text-yellow-600'
    },
    {
      id: 'audio',
      icon: Volume2,
      label: language === 'es' ? 'Escuchar' : 'Listen',
      onClick: onAudio,
      color: 'text-indigo-600'
    }
  ];

  // Mobile: Bottom sheet
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 z-40 animate-in fade-in duration-200" onClick={onClose} />
        
        {/* Bottom Sheet - Optimized for mobile */}
        <div 
          ref={menuRef}
          className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl z-50 animate-in slide-in-from-bottom duration-300 max-h-[70vh]"
        >
          {/* Swipe indicator */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full"></div>
          </div>
          
          {/* Selected verse info */}
          <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {verse.reference}
              </p>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">
              {verse.text}
            </p>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-4 gap-3 p-4 pb-safe-offset-6 overflow-y-auto max-h-[50vh]">
            {actions.map((action) => (
              <button
                key={action.id}
                onClick={action.onClick}
                data-testid={`verse-action-${action.id}`}
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-hover:bg-slate-200 dark:group-hover:bg-slate-700 transition-colors">
                  <action.icon className={`w-5 h-5 ${action.color}`} />
                </div>
                <span className="text-[10px] font-medium text-slate-700 dark:text-slate-300 text-center leading-tight">
                  {action.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </>
    );
  }

  // Desktop: Floating menu
  return (
    <>
      {/* Backdrop (subtle) */}
      <div className="fixed inset-0 z-40" onClick={onClose} />
      
      {/* Floating Menu */}
      <div 
        ref={menuRef}
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 z-50 animate-in zoom-in-95 duration-200 w-[480px] max-w-[90vw]"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {verse.reference}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {verse.translation}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Verse text */}
        <div className="px-6 py-4 max-h-32 overflow-y-auto">
          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            {verse.text}
          </p>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-4 gap-3 p-6 border-t border-slate-200 dark:border-slate-700">
          {actions.map((action) => (
            <button
              key={action.id}
              onClick={action.onClick}
              data-testid={`verse-action-${action.id}`}
              className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
            >
              <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-hover:bg-slate-200 dark:group-hover:bg-slate-700 transition-colors">
                <action.icon className={`w-5 h-5 ${action.color}`} />
              </div>
              <span className="text-xs font-medium text-slate-700 dark:text-slate-300 text-center">
                {action.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default VerseActionMenu;
